import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  ThumbsUp, ThumbsDown, Edit2, Trash2, Star, CheckCircle2, XCircle,
  AlertCircle, Send, FileText, MoreHorizontal, Pencil, X as XIcon
} from "lucide-react";
import { ApprovalBadge, StatusBadge } from "./AssetCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AssetDetailModal({ asset, open, onClose, onUpdate, onDelete, onRefresh, statuses, section, categories, isOwner, currentUserId }) {
  const [feedbackText, setFeedbackText] = useState("");
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editTags, setEditTags] = useState("");
  const [editCategoryId, setEditCategoryId] = useState("");
  const [replaceFile, setReplaceFile] = useState(null);
  const [showVersionPrompt, setShowVersionPrompt] = useState(false);
  const [viewingVersion, setViewingVersion] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalAction, setApprovalAction] = useState("");
  const [approvalComment, setApprovalComment] = useState("");
  const [submitMessage, setSubmitMessage] = useState("");
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  const [editingFeedbackId, setEditingFeedbackId] = useState(null);
  const [editFeedbackText, setEditFeedbackText] = useState("");
  const [menuOpenFb, setMenuOpenFb] = useState(null);
  const textareaRef = useRef(null);

  // Auto-resize comment textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "36px";
      textareaRef.current.style.height = textareaRef.current.scrollHeight + "px";
    }
  }, [feedbackText]);

  if (!asset) return null;

  const isUploader = currentUserId && (asset.created_by_id === currentUserId || asset.created_by === currentUserId);
  const canEditAsset = isUploader || isOwner;

  const startEdit = () => {
    setEditName(asset.name);
    setEditDesc(asset.description || "");
    setEditTags(asset.tags?.join(", ") || "");
    setEditCategoryId(asset.category_id || "");
    setReplaceFile(null);
    setEditing(true);
  };

  const saveEdit = async (isNewVersion = true) => {
    try {
      const res = await axios.put(`${API}/marketing/assets/${asset.id}`, {
        name: editName, description: editDesc,
        tags: editTags.split(",").map(t => t.trim().toLowerCase()).filter(Boolean),
        category_id: editCategoryId,
      });
      if (replaceFile) {
        const fd = new FormData();
        fd.append("file", replaceFile);
        fd.append("is_new_version", isNewVersion ? "true" : "false");
        const fileRes = await axios.post(`${API}/marketing/assets/${asset.id}/replace-file`, fd);
        onUpdate(fileRes.data);
      } else {
        onUpdate(res.data);
      }
      setEditing(false);
      setShowVersionPrompt(false);
      setReplaceFile(null);
      toast.success("Asset updated");
    } catch { toast.error("Failed to update"); }
  };

  const handleSaveClick = () => {
    if (replaceFile) setShowVersionPrompt(true);
    else saveEdit(false);
  };

  // Asset-level voting (no comment required)
  const voteOnAsset = async (thumbs) => {
    try {
      const res = await axios.post(`${API}/marketing/assets/${asset.id}/vote`, { thumbs });
      onUpdate(res.data);
      toast.success(thumbs === "up" ? "Voted up" : "Voted down");
    } catch { toast.error("Failed to vote"); }
  };

  // Add a comment (no thumbs required)
  const addComment = async () => {
    if (!feedbackText.trim()) return;
    try {
      const res = await axios.post(`${API}/marketing/assets/${asset.id}/feedback`, { text: feedbackText, thumbs: "none" });
      onUpdate(res.data);
      setFeedbackText("");
      toast.success("Comment added");
    } catch { toast.error("Failed"); }
  };

  // Edit own comment
  const saveEditFeedback = async (fbId) => {
    try {
      const res = await axios.put(`${API}/marketing/assets/${asset.id}/feedback/${fbId}`, { text: editFeedbackText });
      onUpdate(res.data);
      setEditingFeedbackId(null);
      setEditFeedbackText("");
      toast.success("Comment updated");
    } catch { toast.error("Failed to edit"); }
  };

  // Delete own comment
  const deleteFeedback = async (fbId) => {
    try {
      const res = await axios.delete(`${API}/marketing/assets/${asset.id}/feedback/${fbId}`);
      onUpdate(res.data);
      toast.success("Comment deleted");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete"); }
  };

  // React to a comment
  const reactToComment = async (fbId, reaction) => {
    try {
      const res = await axios.post(`${API}/marketing/assets/${asset.id}/feedback/${fbId}/react`, { reaction });
      onUpdate(res.data);
    } catch { toast.error("Failed"); }
  };

  const toggleFinalize = async () => {
    try {
      const res = await axios.put(`${API}/marketing/assets/${asset.id}`, { is_finalized: !asset.is_finalized });
      onUpdate(res.data);
      toast.success(res.data.is_finalized ? "Marked as finalized" : "Removed from finalized");
    } catch { toast.error("Failed"); }
  };

  const changeStatus = async (newStatus) => {
    try {
      const res = await axios.put(`${API}/marketing/assets/${asset.id}`, { status: newStatus });
      onUpdate(res.data);
      toast.success("Status updated");
    } catch { toast.error("Failed"); }
  };

  const submitForApproval = async () => {
    try {
      const res = await axios.post(`${API}/marketing/assets/${asset.id}/submit-for-approval`, { message: submitMessage });
      onUpdate(res.data);
      setShowSubmitModal(false);
      setSubmitMessage("");
      toast.success("Submitted for approval");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to submit"); }
  };

  const handleReview = async () => {
    try {
      const res = await axios.post(`${API}/marketing/assets/${asset.id}/review`, { action: approvalAction, comment: approvalComment });
      onUpdate(res.data);
      setShowApprovalModal(false);
      setApprovalAction("");
      setApprovalComment("");
      const labels = { approve: "approved", reject: "rejected", request_revision: "revision requested for" };
      toast.success(`Asset ${labels[approvalAction]}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to review"); }
  };

  const previewUrl = viewingVersion ? viewingVersion.file_url : asset.file_url;
  const previewType = viewingVersion ? viewingVersion.file_type : asset.file_type;
  const canSubmitForApproval = asset.approval_status === "draft" || asset.approval_status === "revision_requested" || asset.approval_status === "rejected";
  const canReview = isOwner && asset.approval_status === "pending_review";

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { onClose(); setEditing(false); setViewingVersion(null); setShowVersionPrompt(false); setMenuOpenFb(null); } }}>
      <DialogContent className="sm:max-w-6xl max-h-[90vh] p-0 overflow-hidden [&>button]:hidden" data-testid="asset-lightbox">
        <div className="flex h-[85vh]">
          {/* Left: Preview */}
          <div className="flex-1 bg-stone-950 flex items-center justify-center min-w-0 relative">
            {/* Custom close button - top-left to avoid overlap */}
            <button data-testid="asset-close-btn" onClick={onClose} className="absolute top-3 left-3 z-10 w-8 h-8 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center text-white/80 hover:text-white transition-colors">
              <XIcon className="w-4 h-4" />
            </button>
            {previewType === "image" && (
              <img src={previewUrl} alt={asset.name} className="max-w-full max-h-full object-contain" />
            )}
            {previewType === "video" && (
              <video src={previewUrl} controls className="max-w-full max-h-full" />
            )}
            {previewType === "document" && (
              <iframe src={previewUrl} title={asset.name} className="w-full h-full bg-white" />
            )}
            {viewingVersion && (
              <div className="absolute bottom-4 left-4 bg-blue-600 text-white text-xs px-3 py-1.5 rounded-full flex items-center gap-2">
                Viewing v{viewingVersion.version}
                <button onClick={() => setViewingVersion(null)} className="hover:text-amber-400">Back to latest</button>
              </div>
            )}
          </div>

          {/* Right: Details Panel */}
          <div className="w-80 bg-white border-l border-stone-200 flex flex-col">
            {/* Header */}
            <div className="px-4 py-3 border-b border-stone-100">
              {editing ? (
                <div className="space-y-2">
                  <Input value={editName} onChange={e => setEditName(e.target.value)} placeholder="Name" className="text-sm" />
                  <Input value={editDesc} onChange={e => setEditDesc(e.target.value)} placeholder="Description" className="text-sm" />
                  <Input value={editTags} onChange={e => setEditTags(e.target.value)} placeholder="Tags (comma separated)" className="text-sm" />
                  {categories.length > 0 && (
                    <select value={editCategoryId} onChange={e => setEditCategoryId(e.target.value)} className="w-full border rounded px-2 py-1 text-sm">
                      <option value="">No category</option>
                      {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  )}
                  <div className="flex items-center gap-2 p-2 bg-stone-50 rounded">
                    <input type="file" onChange={e => setReplaceFile(e.target.files?.[0] || null)} className="text-xs" />
                    <span className="text-[10px] text-stone-400">Replace file (optional)</span>
                  </div>
                  <div className="flex gap-2 pt-1">
                    <Button variant="outline" size="sm" onClick={() => { setEditing(false); setReplaceFile(null); }} className="flex-1 text-xs">Cancel</Button>
                    <Button size="sm" onClick={handleSaveClick} className="flex-1 text-xs bg-stone-900 text-white hover:bg-stone-800">Save</Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-start justify-between mb-0.5">
                    <h3 className="text-base font-semibold text-stone-900 pr-2" style={{ fontFamily: "'Playfair Display', serif" }}>{asset.name}</h3>
                    {canEditAsset && (
                      <div className="flex gap-1 flex-shrink-0">
                        <button data-testid="asset-edit-btn" onClick={startEdit} className="p-1 text-stone-400 hover:text-stone-700"><Edit2 className="w-4 h-4" /></button>
                        <button data-testid="asset-delete-btn" onClick={() => { onDelete(asset.id); onClose(); }} className="p-1 text-stone-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    )}
                  </div>
                  {asset.description && <p className="text-xs text-stone-500 mb-2">{asset.description}</p>}

                  <div className="flex flex-wrap items-center gap-1.5 mb-2">
                    {asset.version > 1 && <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 font-medium">v{asset.version}</span>}
                    {asset.status && statuses.length > 0 && <StatusBadge status={asset.status} statuses={statuses} />}
                    {asset.is_finalized && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium flex items-center gap-1"><Star className="w-3 h-3" /> Finalized</span>}
                  </div>

                  {/* Asset-level Voting */}
                  <div className="flex items-center gap-3 p-2 bg-stone-50 rounded-lg mb-2">
                    <button data-testid="asset-vote-up" onClick={() => voteOnAsset("up")} className="flex items-center gap-1 text-xs text-stone-500 hover:text-emerald-600 transition-colors">
                      <ThumbsUp className="w-3.5 h-3.5" /> <span>{asset.thumbs_up || 0}</span>
                    </button>
                    <button data-testid="asset-vote-down" onClick={() => voteOnAsset("down")} className="flex items-center gap-1 text-xs text-stone-500 hover:text-red-500 transition-colors">
                      <ThumbsDown className="w-3.5 h-3.5" /> <span>{asset.thumbs_down || 0}</span>
                    </button>
                  </div>

                  {/* Approval Status */}
                  <div className="p-2.5 bg-stone-50 rounded-lg mb-2">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-stone-600">Approval Status</span>
                      <ApprovalBadge status={asset.approval_status || "draft"} />
                    </div>
                    <div className="flex gap-1.5">
                      {canSubmitForApproval && (
                        <Button size="sm" onClick={() => setShowSubmitModal(true)} className="flex-1 text-[11px] h-7 bg-blue-600 hover:bg-blue-700">
                          <Send className="w-3 h-3 mr-1" /> Submit for Review
                        </Button>
                      )}
                      {canReview && (
                        <>
                          <Button size="sm" variant="outline" onClick={() => { setApprovalAction("approve"); setShowApprovalModal(true); }} className="flex-1 text-[11px] h-7 text-emerald-600 border-emerald-200 hover:bg-emerald-50">
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Approve
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => { setApprovalAction("reject"); setShowApprovalModal(true); }} className="flex-1 text-[11px] h-7 text-red-600 border-red-200 hover:bg-red-50">
                            <XCircle className="w-3 h-3 mr-1" /> Reject
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => { setApprovalAction("request_revision"); setShowApprovalModal(true); }} className="text-[11px] h-7 text-orange-600 border-orange-200 hover:bg-orange-50">
                            <AlertCircle className="w-3 h-3" />
                          </Button>
                        </>
                      )}
                    </div>
                    {asset.submitted_by && <p className="text-[10px] text-stone-400 mt-1.5">Submitted by {asset.submitted_by} {asset.submitted_at && `on ${new Date(asset.submitted_at).toLocaleDateString()}`}</p>}
                    {asset.reviewed_by && <p className="text-[10px] text-stone-400">Reviewed by {asset.reviewed_by} {asset.reviewed_at && `on ${new Date(asset.reviewed_at).toLocaleDateString()}`}</p>}
                  </div>

                  {statuses.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {statuses.map(s => (
                        <Button key={s.value} size="sm" variant={asset.status === s.value ? "default" : "outline"} className="text-[11px] h-6 px-2" onClick={() => changeStatus(s.value)}>
                          {s.label}
                        </Button>
                      ))}
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={toggleFinalize} className="text-xs h-7">
                      <Star className={`w-3.5 h-3.5 mr-1 ${asset.is_finalized ? "text-amber-500" : ""}`} fill={asset.is_finalized ? "currentColor" : "none"} />
                      {asset.is_finalized ? "Unfinalize" : "Finalize"}
                    </Button>
                  </div>

                  {asset.tags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {asset.tags.map(t => <span key={t} className="text-[10px] px-1.5 py-0.5 bg-stone-100 text-stone-500 rounded-full">{t}</span>)}
                    </div>
                  )}

                  <p className="text-[10px] text-stone-400 mt-2">Uploaded by {asset.created_by} &middot; {new Date(asset.created_at).toLocaleDateString()}</p>
                </>
              )}
            </div>

            {/* Approval History */}
            {asset.approval_history?.length > 0 && !editing && (
              <div className="px-4 py-3 border-b border-stone-100">
                <h4 className="text-xs font-semibold text-stone-700 mb-2">Approval History</h4>
                <div className="space-y-1.5 max-h-24 overflow-y-auto">
                  {asset.approval_history.map((h) => (
                    <div key={h.id} className="p-2 bg-stone-50 rounded text-xs">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        {h.action === "submitted" && <Send className="w-3 h-3 text-blue-500" />}
                        {h.action === "approve" && <CheckCircle2 className="w-3 h-3 text-emerald-500" />}
                        {h.action === "reject" && <XCircle className="w-3 h-3 text-red-500" />}
                        {h.action === "request_revision" && <AlertCircle className="w-3 h-3 text-orange-500" />}
                        <span className="font-medium capitalize">{h.action.replace("_", " ")}</span>
                        <span className="text-stone-400">by {h.user_name || h.user_email}</span>
                      </div>
                      {(h.message || h.comment) && <p className="text-stone-600 pl-4">{h.message || h.comment}</p>}
                      <p className="text-[10px] text-stone-400 pl-4">{new Date(h.timestamp).toLocaleString()}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Version History */}
            {asset.versions?.length > 0 && !editing && (
              <div className="px-4 py-3 border-b border-stone-100">
                <h4 className="text-xs font-semibold text-stone-700 mb-2">Version History</h4>
                <div className="space-y-1.5 max-h-28 overflow-y-auto">
                  <div className={`flex items-center gap-2 p-1.5 rounded text-xs cursor-pointer ${!viewingVersion ? "bg-blue-50 text-blue-700" : "hover:bg-stone-50 text-stone-600"}`} onClick={() => setViewingVersion(null)}>
                    <span className="font-medium">v{asset.version}</span>
                    <span className="text-stone-400">(current)</span>
                  </div>
                  {asset.versions.map((v, i) => (
                    <div key={i} className={`flex items-center gap-2 p-1.5 rounded text-xs cursor-pointer ${viewingVersion?.version === v.version ? "bg-blue-50 text-blue-700" : "hover:bg-stone-50 text-stone-600"}`} onClick={() => setViewingVersion(v)}>
                      <span className="font-medium">v{v.version}</span>
                      <span className="text-stone-400 truncate">{new Date(v.replaced_at).toLocaleDateString()} by {v.replaced_by}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Comments List */}
            <div className="flex-1 overflow-y-auto p-4">
              <h4 className="text-sm font-semibold text-stone-900 mb-3">Comments</h4>
              {asset.feedback?.length > 0 ? (
                <div className="space-y-2">
                  {asset.feedback.map(fb => {
                    const isOwnComment = currentUserId && fb.user_id === currentUserId;
                    const reactions = fb.reactions || [];
                    const upCount = reactions.filter(r => r.reaction === "up").length;
                    const downCount = reactions.filter(r => r.reaction === "down").length;

                    if (editingFeedbackId === fb.id) {
                      return (
                        <div key={fb.id} className="p-2.5 bg-stone-50 rounded-lg space-y-2">
                          <Textarea value={editFeedbackText} onChange={e => setEditFeedbackText(e.target.value)} className="text-sm min-h-[36px]" rows={2} />
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" onClick={() => { setEditingFeedbackId(null); setEditFeedbackText(""); }} className="text-xs h-7">Cancel</Button>
                            <Button size="sm" onClick={() => saveEditFeedback(fb.id)} className="text-xs h-7 bg-stone-900 text-white hover:bg-stone-800">Save</Button>
                          </div>
                        </div>
                      );
                    }

                    return (
                      <div key={fb.id} data-testid={`comment-${fb.id}`} className="p-2.5 bg-stone-50 rounded-lg group relative">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="text-sm text-stone-700">{fb.text || <em className="text-stone-400">No comment</em>}</p>
                            <p className="text-[10px] text-stone-400 mt-1">
                              {fb.user_email} &middot; {new Date(fb.created_at).toLocaleDateString()}
                              {fb.edited_at && <span className="italic"> (edited)</span>}
                            </p>
                          </div>
                          {/* Actions menu for own comments */}
                          {(isOwnComment || isOwner) && (
                            <div className="relative flex-shrink-0">
                              <button onClick={() => setMenuOpenFb(menuOpenFb === fb.id ? null : fb.id)} className="p-1 text-stone-300 hover:text-stone-600 opacity-0 group-hover:opacity-100 transition-opacity">
                                <MoreHorizontal className="w-3.5 h-3.5" />
                              </button>
                              {menuOpenFb === fb.id && (
                                <div className="absolute right-0 top-6 bg-white border border-stone-200 rounded-lg shadow-lg z-20 py-1 min-w-[100px]">
                                  {isOwnComment && (
                                    <button data-testid={`edit-comment-${fb.id}`} onClick={() => { setEditingFeedbackId(fb.id); setEditFeedbackText(fb.text || ""); setMenuOpenFb(null); }} className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-stone-700 hover:bg-stone-50">
                                      <Pencil className="w-3 h-3" /> Edit
                                    </button>
                                  )}
                                  <button data-testid={`delete-comment-${fb.id}`} onClick={() => { deleteFeedback(fb.id); setMenuOpenFb(null); }} className="flex items-center gap-2 w-full px-3 py-1.5 text-xs text-red-600 hover:bg-red-50">
                                    <Trash2 className="w-3 h-3" /> Delete
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        {/* Comment reactions */}
                        <div className="flex items-center gap-2 mt-1.5">
                          <button onClick={() => reactToComment(fb.id, "up")} className="flex items-center gap-0.5 text-[10px] text-stone-400 hover:text-emerald-600 transition-colors">
                            <ThumbsUp className="w-3 h-3" /> {upCount > 0 && upCount}
                          </button>
                          <button onClick={() => reactToComment(fb.id, "down")} className="flex items-center gap-0.5 text-[10px] text-stone-400 hover:text-red-500 transition-colors">
                            <ThumbsDown className="w-3 h-3" /> {downCount > 0 && downCount}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-stone-400 text-center py-4">No comments yet — be the first!</p>
              )}
            </div>

            {/* Comment Input (sticky bottom) */}
            <div className="p-3 border-t border-stone-100 bg-white">
              <div className="flex gap-1.5 items-end">
                <textarea
                  ref={textareaRef}
                  data-testid="feedback-input"
                  placeholder="Add a comment..."
                  value={feedbackText}
                  onChange={e => setFeedbackText(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey && feedbackText.trim()) { e.preventDefault(); addComment(); } }}
                  className="flex-1 text-sm border border-stone-200 rounded-lg px-3 py-2 resize-none overflow-hidden focus:outline-none focus:ring-2 focus:ring-stone-400 focus:ring-offset-0"
                  style={{ minHeight: "36px", maxHeight: "120px" }}
                  rows={1}
                />
                <Button size="sm" onClick={addComment} disabled={!feedbackText.trim()} data-testid="send-comment-btn" className="h-9 w-9 p-0 bg-stone-900 text-white hover:bg-stone-800 flex-shrink-0">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Version Prompt Modal */}
        {showVersionPrompt && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 max-w-sm mx-4 shadow-xl">
              <h3 className="text-base font-semibold text-stone-900 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>New Version?</h3>
              <p className="text-sm text-stone-600 mb-4">You're replacing the file. Would you like to save this as a new version?</p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => saveEdit(false)} className="flex-1 text-xs">Overwrite</Button>
                <Button onClick={() => saveEdit(true)} className="flex-1 text-xs bg-stone-900 text-white hover:bg-stone-800">Save as New Version</Button>
              </div>
            </div>
          </div>
        )}

        {/* Submit for Approval Modal */}
        {showSubmitModal && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 max-w-sm mx-4 shadow-xl">
              <h3 className="text-base font-semibold text-stone-900 mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>Submit for Approval</h3>
              <p className="text-sm text-stone-600 mb-3">This asset will be sent for review by an owner/admin.</p>
              <Textarea value={submitMessage} onChange={e => setSubmitMessage(e.target.value)} placeholder="Add a message (optional)..." className="mb-4 text-sm" rows={3} />
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => { setShowSubmitModal(false); setSubmitMessage(""); }} className="flex-1 text-xs">Cancel</Button>
                <Button onClick={submitForApproval} className="flex-1 text-xs bg-blue-600 text-white hover:bg-blue-700"><Send className="w-3 h-3 mr-1" /> Submit</Button>
              </div>
            </div>
          </div>
        )}

        {/* Review Modal */}
        {showApprovalModal && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-6 max-w-sm mx-4 shadow-xl">
              <h3 className="text-base font-semibold text-stone-900 mb-2 capitalize" style={{ fontFamily: "'Playfair Display', serif" }}>
                {approvalAction === "approve" && "Approve Asset"}
                {approvalAction === "reject" && "Reject Asset"}
                {approvalAction === "request_revision" && "Request Revision"}
              </h3>
              <p className="text-sm text-stone-600 mb-3">
                {approvalAction === "approve" && "This will mark the asset as approved and finalized."}
                {approvalAction === "reject" && "The submitter will be notified of the rejection."}
                {approvalAction === "request_revision" && "Ask the submitter to make changes before resubmitting."}
              </p>
              <Textarea value={approvalComment} onChange={e => setApprovalComment(e.target.value)} placeholder={approvalAction === "approve" ? "Add a comment (optional)..." : "Add feedback or reason..."} className="mb-4 text-sm" rows={3} />
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => { setShowApprovalModal(false); setApprovalComment(""); setApprovalAction(""); }} className="flex-1 text-xs">Cancel</Button>
                <Button onClick={handleReview} className={`flex-1 text-xs text-white ${approvalAction === "approve" ? "bg-emerald-600 hover:bg-emerald-700" : approvalAction === "reject" ? "bg-red-600 hover:bg-red-700" : "bg-orange-600 hover:bg-orange-700"}`}>
                  {approvalAction === "approve" && <><CheckCircle2 className="w-3 h-3 mr-1" /> Approve</>}
                  {approvalAction === "reject" && <><XCircle className="w-3 h-3 mr-1" /> Reject</>}
                  {approvalAction === "request_revision" && <><AlertCircle className="w-3 h-3 mr-1" /> Request Revision</>}
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
